/**
 * AVTR Scroll Reveal + Smooth Scroll
 * AvatarDesigns Design System v1.0
 *
 * - IntersectionObserver para scroll reveal
 * - Lenis para smooth scroll (cargado desde CDN si disponible)
 * - Stagger en product grids
 * - Lazy image load enhancement
 */

(function () {
  'use strict';

  /* ── 1. SMOOTH SCROLL CON LENIS ── */
  function initLenis() {
    if (typeof Lenis === 'undefined') return;

    const lenis = new Lenis({
      duration: 1.15,
      easing: function (t) {
        return Math.min(1, 1.001 - Math.pow(2, -10 * t));
      },
      smoothTouch: false,
      touchMultiplier: 2.0,
      infinite: false,
    });

    /* La página crece al cargar fotos/videos: si Lenis no recalcula el alto, el scroll se frena antes del final */
    var lastHeight = 0;
    function raf(time) {
      var h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        if (typeof lenis.resize === 'function') lenis.resize();
      }
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    window.__avtrLenis = lenis;
  }

  /* ── 2. INTERSECTION OBSERVER ── */
  var revealObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var el = entry.target;
        var delay = parseInt(el.dataset.avtrDelay || '0', 10);

        setTimeout(function () {
          el.classList.add('avtr-revealed');
        }, delay);

        revealObserver.unobserve(el);
      });
    },
    {
      threshold: 0.10,
      rootMargin: '0px 0px -48px 0px',
    }
  );

  /* ── 3. STAGGER GRIDS ── */
  var staggerObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('avtr-stagger--visible');
        staggerObserver.unobserve(entry.target);
      });
    },
    {
      threshold: 0.06,
      rootMargin: '0px 0px -32px 0px',
    }
  );

  /* ── 4. APPLY STAGGER TO PRODUCT GRIDS ── */
  function applyStaggerToGrids() {
    var grids = document.querySelectorAll(
      '.collection .grid:not(.avtr-stagger-applied), ' +
      '.featured-collection .grid:not(.avtr-stagger-applied), ' +
      '.related-products .grid:not(.avtr-stagger-applied)'
    );

    grids.forEach(function (grid) {
      grid.classList.add('avtr-stagger', 'avtr-stagger-applied');
      staggerObserver.observe(grid);
    });
  }

  /* ── 5. APPLY REVEAL TO SECTIONS ── */
  function applyReveal() {
    // Secciones de slide-in
    var sections = document.querySelectorAll(
      '.avtr-slide-in-section:not(.avtr-init), ' +
      '[data-avtr-reveal]:not(.avtr-init)'
    );

    sections.forEach(function (el) {
      el.classList.add('avtr-init');
      revealObserver.observe(el);
    });
  }

  /* ── 6. LAZY IMAGE ENHANCEMENT — blur-up ── */
  function enhanceLazyImages() {
    var images = document.querySelectorAll('img[loading="lazy"]:not(.avtr-blur-init)');
    images.forEach(function (img) {
      img.classList.add('avtr-blur-init', 'avtr-img-blur');

      /* Already loaded (browser cache / fast connection) */
      if (img.complete && img.naturalWidth > 0) {
        img.classList.remove('avtr-img-blur');
        img.classList.add('loaded');
        return;
      }

      img.addEventListener('load', function () {
        img.classList.remove('avtr-img-blur');
        img.classList.add('loaded');
      }, { once: true });

      img.addEventListener('error', function () {
        img.classList.remove('avtr-img-blur');
      }, { once: true });
    });
  }

  /* Imágenes lazy añadidas después del load (filtros, búsqueda, recomendados,
     Alpine) quedarían con opacity:0 sin la clase .loaded */
  function observeLazyImages() {
    if (!('MutationObserver' in window)) return;
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        enhanceLazyImages();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── 7. AVTR QUICK ADD OVERLAY ── */
  function injectQuickAddOverlays() {
    var cards = document.querySelectorAll(
      '.card-wrapper.product-card-wrapper:not(.avtr-overlay-applied)'
    );

    cards.forEach(function (wrapper) {
      wrapper.classList.add('avtr-overlay-applied');

      var inner = wrapper.querySelector('.card__inner');
      if (!inner) return;

      // Only if there's no existing quick add button
      var existingQuickAdd = wrapper.querySelector('.quick-add__submit');
      if (existingQuickAdd) return;

      var overlay = document.createElement('div');
      overlay.className = 'avtr-quick-add-overlay';
      overlay.setAttribute('aria-hidden', 'true');

      // Get product URL from card link
      var cardLink = wrapper.querySelector('a.full-unstyled-link, a[href*="/products/"]');
      var href = cardLink ? cardLink.getAttribute('href') : '#';

      overlay.innerHTML =
        '<a href="' + href + '" style="color:inherit;text-decoration:none;display:block;">' +
        'VER PRODUCTO →' +
        '</a>';

      inner.appendChild(overlay);
    });
  }

  /* ── 8. HEADER SCROLL GLASSMORPHISM ── */
  function initHeaderScroll() {
    var header = document.querySelector('.section-header, sticky-header');
    if (!header) return;

    var lastScroll = 0;
    var ticking = false;

    function onScroll() {
      lastScroll = window.scrollY;
      if (!ticking) {
        requestAnimationFrame(function () {
          var headerWrapper = header.querySelector('.header-wrapper');
          if (!headerWrapper) { ticking = false; return; }

          if (lastScroll > 60) {
            headerWrapper.setAttribute('data-scrolled', 'true');
            // Enhance glassmorphism visually
            headerWrapper.style.setProperty('--header-scroll-bg', 'rgba(0,0,0,0.94)');
          } else {
            headerWrapper.removeAttribute('data-scrolled');
            headerWrapper.style.removeProperty('--header-scroll-bg');
          }

          ticking = false;
        });
        ticking = true;
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // Check on init
  }

  /* ── 9. CART ICON PULSE on add ── */
  function initCartPulse() {
    document.addEventListener('cart:item-added', function () {
      var cartIcon = document.querySelector('#cart-icon-bubble, .header__icon--cart');
      if (!cartIcon) return;
      cartIcon.classList.remove('cart-icon-pulse');
      void cartIcon.offsetWidth; // reflow
      cartIcon.classList.add('cart-icon-pulse');
      setTimeout(function () {
        cartIcon.classList.remove('cart-icon-pulse');
      }, 450);
    });
  }

  /* ── 10. ANNOUNCEMENT BAR TICKER (si no usa CSS) ── */
  function initAnnouncementBar() {
    // La animación CSS ya maneja esto, solo nos aseguramos de
    // que el elemento esté visible
    var bar = document.querySelector('.announcement-bar-section');
    if (bar) {
      bar.style.display = 'block';
    }
  }

  /* ── 11. PDP TRUST BADGES ── */
  function injectTrustBadges() {
    // Only on product pages
    var formButtons = document.querySelector('.product-form__buttons');
    if (!formButtons) return;

    // Don't inject twice
    if (document.querySelector('.avtr-trust-strip')) return;

    var strip = document.createElement('div');
    strip.className = 'avtr-trust-strip';
    strip.innerHTML =
      '<div class="avtr-trust-item">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>' +
        '<span class="avtr-trust-item__text">Envío gratis +$300k</span>' +
      '</div>' +
      '<div class="avtr-trust-item">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' +
        '<span class="avtr-trust-item__text">Calidad garantizada</span>' +
      '</div>' +
      '<div class="avtr-trust-item">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>' +
        '<span class="avtr-trust-item__text">Pago seguro</span>' +
      '</div>' +
      '<div class="avtr-trust-item">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>' +
        '<span class="avtr-trust-item__text">Entrega 1-3 días</span>' +
      '</div>';

    formButtons.parentNode.insertBefore(strip, formButtons.nextSibling);
  }

  /* ── 12. MOBILE STICKY ATC ── */
  function initStickyATC() {
    // Only on mobile product pages
    if (window.innerWidth > 749) return;
    var submitBtn = document.querySelector('.product-form__submit');
    if (!submitBtn) return;
    if (document.querySelector('.avtr-sticky-atc')) return;

    var productTitle = document.querySelector('.product__title h1, .product__title h2');
    var priceEl = document.querySelector('.price--large .price-item--sale, .price--large .price-item--regular');

    var bar = document.createElement('div');
    bar.className = 'avtr-sticky-atc';
    bar.innerHTML =
      '<div class="avtr-sticky-atc__inner">' +
        '<div class="avtr-sticky-atc__info">' +
          '<span class="avtr-sticky-atc__title">' + (productTitle ? productTitle.textContent.trim() : '') + '</span>' +
          '<span class="avtr-sticky-atc__price">' + (priceEl ? priceEl.textContent.trim() : '') + '</span>' +
        '</div>' +
        '<button class="avtr-sticky-atc__btn" type="button">AGREGAR</button>' +
      '</div>';

    document.body.appendChild(bar);

    // Show bar when ATC button scrolls out of view
    var observer = new IntersectionObserver(
      function(entries) {
        entries.forEach(function(entry) {
          if (!entry.isIntersecting) {
            bar.classList.add('is-visible');
          } else {
            bar.classList.remove('is-visible');
          }
        });
      },
      { threshold: 0 }
    );
    observer.observe(submitBtn);

    // Proxy click to main submit button
    bar.querySelector('.avtr-sticky-atc__btn').addEventListener('click', function() {
      submitBtn.click();
    });
  }

  /* ── 13. CART FREE SHIPPING BAR ── */
  function injectFreeShippingBar() {
    var cartItems = document.querySelector('.cart-items, cart-items');
    if (!cartItems) return;
    if (document.querySelector('.avtr-free-shipping')) return;

    var FREE_THRESHOLD = 300000; // COP

    // Get subtotal from page
    var subtotalEl = document.querySelector('.totals__subtotal-value');
    if (!subtotalEl) return;

    // Parse price - remove non-numeric except decimal
    var raw = subtotalEl.textContent.replace(/[^0-9,\.]/g, '').replace(',', '');
    var subtotal = parseFloat(raw) || 0;

    var remaining = Math.max(0, FREE_THRESHOLD - subtotal);
    var pct = Math.min(100, (subtotal / FREE_THRESHOLD) * 100);

    var msg = remaining > 0
      ? ('Faltan $' + remaining.toLocaleString('es-CO') + ' para envío gratis')
      : '¡Envío gratis desbloqueado!';

    var bar = document.createElement('div');
    bar.className = 'avtr-free-shipping';
    bar.innerHTML =
      '<span class="avtr-free-shipping__text">' + msg + '</span>' +
      '<div class="avtr-free-shipping__bar-bg">' +
        '<div class="avtr-free-shipping__bar-fill" style="width:' + pct + '%"></div>' +
      '</div>';

    var header = document.querySelector('.cart-drawer__header');
    if (header) {
      header.parentNode.insertBefore(bar, header.nextSibling);
    }
  }

  /* ── 14. HANDLE SHOPIFY SECTION EVENTS (ajax reloads) ── */
  document.addEventListener('shopify:section:load', function () {
    setTimeout(function () {
      applyStaggerToGrids();
      applyReveal();
      enhanceLazyImages();
      injectQuickAddOverlays();
      injectTrustBadges();
      injectFreeShippingBar();
    }, 100);
  });

  // Re-inject free shipping bar when cart updates
  document.addEventListener('cart:updated', function () {
    var existing = document.querySelector('.avtr-free-shipping');
    if (existing) existing.remove();
    setTimeout(injectFreeShippingBar, 200);
  });

  /* ── INIT ── */
  function init() {
    initLenis();
    applyStaggerToGrids();
    applyReveal();
    enhanceLazyImages();
    observeLazyImages();
    injectQuickAddOverlays();
    initHeaderScroll();
    initCartPulse();
    initAnnouncementBar();
    injectTrustBadges();
    initStickyATC();
    // Free shipping bar needs cart drawer to be open - triggered on open
    document.addEventListener('click', function(e) {
      if (e.target.closest('[href="#cart-drawer"], .header__icon--cart, #cart-icon-bubble')) {
        setTimeout(injectFreeShippingBar, 400);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
